import axios from 'axios';
import { detectInitialLocale } from '../i18n';
import { isPaywallError, openPaywall } from '../utils/paywall';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Default axios instance — attaches the logged-in user's JWT automatically
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

// Cold-start / transient-network resilience: a Cloud Run instance that scaled to
// zero needs a few seconds to boot and reconnect to MongoDB. A request that lands
// in that window fails outright (network error or 5xx) even though the exact same
// request would succeed moments later. Retry once, on every axios instance, before
// giving up — this is what turns a guest's very first page load from "connection
// trouble" into a slightly slower but successful load.
const attachResilience = (instance) => {
  instance.interceptors.response.use(
    (res) => res,
    async (err) => {
      const status = err.response?.status;
      const config = err.config || {};

      if (status === 429 && !config._retried) {
        const retryAfter = Number(err.response?.data?.retryAfter);
        // Only worth an automatic retry when the real wait is short. Every
        // limiter in this app reports a retryAfter far longer than we're
        // willing to block the UI for (auth/rsvp/galleryAccess: 900s,
        // seat: 300s, global: 60s) — silently truncating that to a ~10s wait
        // and retrying anyway just re-hits the same 429 immediately, doubling
        // the request against that limiter's quota and adding a pointless
        // delay before the user ever sees the real error.
        const isLongWait = Number.isFinite(retryAfter) && retryAfter > 10;
        if (!isLongWait) {
          config._retried = true;
          const delay = (Number.isFinite(retryAfter) ? retryAfter : 3) * 1000;
          await new Promise((r) => setTimeout(r, delay));
          return instance(config);
        }
      }

      const isTransient = !err.response || [502, 503, 504].includes(status);
      if (isTransient && !config._retried && config.method === 'get') {
        config._retried = true;
        await new Promise((r) => setTimeout(r, 1500));
        return instance(config);
      }

      if (status === 401) {
        const url = config.url || '';
        const isAuthEndpoint = url.includes('/auth/');
        const isGalleryPublic = url.includes('/galleries/access') || url.includes('/galleries/view/');
        if (!isAuthEndpoint && !isGalleryPublic) {
          localStorage.removeItem('token');
          if (!window.location.pathname.startsWith('/auth') && !window.location.pathname.startsWith('/gallery')) {
            window.location.href = '/auth/login';
          }
        }
      }
      // Free account tried to get a link: open the plan picker wherever it happened.
      if (isPaywallError(err)) openPaywall(err.response.data.action);
      return Promise.reject(err);
    }
  );
};

attachResilience(api);

// ─── Auth ──────────────────────────────────────────────────────────────────
export const userApi = {
  getPlanOverview: () => api.get('/users/plan-overview'),
  recalcStorage: () => api.post('/users/recalc-storage'),
  updateAdConsent: (signals) => api.put('/users/ad-consent', signals),
};

export const adminApi = {
  getStats: () => api.get('/admin/stats'),
  listUsers: (params) => api.get('/admin/users', { params }),
  updateUser: (id, body) => api.patch(`/admin/users/${id}`, body),
};

export const authApi = {
  register:       (data)            => api.post('/auth/register', data),
  login:          (data)            => api.post('/auth/login', data),
  forgotPassword: (email)           => api.post('/auth/forgot-password', { email }),
  resetPassword:  (token, password) => api.post(`/auth/reset-password/${token}`, { password }),
  getMe:          ()                => api.get('/auth/me'),
  verifyEmail:    (token)           => api.get(`/auth/verify-email/${encodeURIComponent(token)}`),
  resendVerification: ()            => api.post('/auth/resend-verification'),
};

export const stripeApi = {
  getPrices: () => api.get('/stripe/prices'),
  /** @param {'month'|'year'} [billingInterval] default month */
  /** @param {string} [returnTo] path to redirect after successful payment */
  /** @param {boolean} [momentsAddon] add Live Moments to a couples plan */
  createCheckoutSession: (planId, billingInterval = 'month', returnTo, momentsAddon = false) =>
    api.post('/stripe/create-checkout-session', {
      planId, billingInterval, returnTo, momentsAddon,
      // Site language → the sample invitation created after purchase uses it.
      locale: detectInitialLocale(),
    }),
  createBillingPortal: () => api.post('/stripe/billing-portal'),
};

export const couponApi = {
  redeem: (code) => api.post('/coupons/redeem', { code }),
};

// ─── Weddings ──────────────────────────────────────────────────────────────
export const weddingApi = {
  getAll:       ()         => api.get('/weddings'),
  create:       (data)     => api.post('/weddings', data),
  duplicate:    (id)       => api.post(`/weddings/${id}/duplicate`),
  /** First-purchase sample draft (created once; returns it afterwards). */
  createSample: (locale)   => api.post('/weddings/sample', { locale }),
  getById:      (id)       => api.get(`/weddings/${id}`),
  getPublicById:(id)       => api.get(`/weddings/public/${id}`),
  getBySlug:    (slug)     => api.get(`/weddings/slug/${slug}`),
  update:       (id, data) => api.put(`/weddings/${id}`, data),
  delete:       (id)       => api.delete(`/weddings/${id}`),
  uploadCover:  (id, file) => {
    const form = new FormData();
    form.append('cover', file);
    return api.post(`/weddings/${id}/cover`, form, { timeout: 120000 });
  },
  uploadInvitationOverlay: (id, file) => {
    const form = new FormData();
    form.append('overlay', file);
    return api.post(`/weddings/${id}/invitation-overlay`, form, { timeout: 120000 });
  },
  uploadSectionBackground: (id, file, sectionKey) => {
    const form = new FormData();
    form.append('sectionBg', file);
    if (sectionKey) form.append('sectionKey', sectionKey);
    return api.post(`/weddings/${id}/section-background`, form, { timeout: 120000 });
  },
  uploadIntroVideo: (id, file, onProgress) => {
    const form = new FormData();
    form.append('introVideo', file);
    return api.post(`/weddings/${id}/intro-video`, form, {
      timeout: 300000,
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },
  uploadAudioTrack: (id, file, onProgress) => {
    const form = new FormData();
    form.append('audioTrack', file);
    return api.post(`/weddings/${id}/audio-track`, form, {
      timeout: 180000,
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },
  publish:         (id) => api.put(`/weddings/${id}/publish`),
  sendInvitations: (id) => api.post(`/weddings/${id}/send-invitations`),
  addCollaborator: (id, email) => api.post(`/weddings/${id}/collaborators`, { email }),
  removeCollaborator: (id, userId) => api.delete(`/weddings/${id}/collaborators/${userId}`),
  linkGallery: (weddingId, galleryId) => api.post(`/weddings/${weddingId}/link-gallery`, { galleryId }),
  unlinkGallery: (weddingId, galleryId) =>
    api.delete(`/weddings/${weddingId}/link-gallery`, { data: { galleryId } }),
  getLinkedGalleryPhotos: (weddingId, params) => api.get(`/weddings/${weddingId}/gallery-photos`, { params }),
};

// ─── Galleries ─────────────────────────────────────────────────────────────
export const galleryApi = {
  getAll:      ()         => api.get('/galleries'),
  create:      (data)     => api.post('/galleries', data),
  getById:     (id)       => api.get(`/galleries/${id}`),
  update:      (id, data) => api.put(`/galleries/${id}`, data),
  updateDesign:(id, data) => api.put(`/galleries/${id}/design`, data),
  getStats:    (id)       => api.get(`/galleries/${id}/stats`),
  uploadCover: (id, file) => {
    const form = new FormData();
    form.append('cover', file, file.name || 'cover.jpg');
    return api.post(`/galleries/${id}/cover`, form, { timeout: 120000 });
  },
  delete:      (id)       => api.delete(`/galleries/${id}`),
  share:       (id, data) => api.post(`/galleries/${id}/share`, data),
  getShareInfo:(id)       => api.get(`/galleries/${id}/share-info`),
  // Public — no user auth needed
  access:      (shareToken, password) => api.post('/galleries/access', { shareToken, password }),
  viewByToken: (shareToken)           => api.get(`/galleries/view/${shareToken}`),
  requestExtension: (shareToken)      => api.post('/galleries/request-extension', { shareToken }),
};

// ─── Photos ────────────────────────────────────────────────────────────────
export const photoApi = {
  getByGallery:      (galleryId, params) => api.get(`/photos/gallery/${galleryId}`, { params }),
  upload:            (galleryId, form, onProgress) =>
    api.post(`/photos/upload/${galleryId}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
      timeout: 300000,
    }),
  togglePrint:       (id, data)          => api.patch(`/photos/${id}/print-select`, data),
  getPrintList:      (galleryId, format = 'json') =>
    api.get(`/photos/gallery/${galleryId}/print-list`, {
      params: { format },
      responseType: format === 'csv' ? 'blob' : 'json',
    }),
  getClientSelections: (galleryId, format = 'json') =>
    api.get(`/photos/gallery/${galleryId}/client-selections`, {
      params: { format },
      responseType: format === 'csv' ? 'blob' : 'json',
    }),
  // target: 'favorites' | 'prints' | 'all'
  resetClientSelections: (galleryId, target) =>
    api.delete(`/photos/gallery/${galleryId}/client-selections`, { params: { target } }),
  getScenes:   (galleryId)            => api.get(`/photos/gallery/${galleryId}/scenes`),
  updateScene: (id, scene)            => api.put(`/photos/${id}/scene`, { scene }),
  bulkScene:   (photoIds, scene)      => api.patch('/photos/bulk-scene', { photoIds, scene }),
  delete:      (id)                   => api.delete(`/photos/${id}`),
  downloadPhoto: (id, onProgress) =>
    api.get(`/photos/${id}/owner-download`, { responseType: 'blob', timeout: 60000, onDownloadProgress: onProgress }),
  downloadPrintSelection: (galleryId, onProgress) =>
    api.get(`/photos/gallery/${galleryId}/download-print-selection`, { responseType: 'blob', timeout: 600000, onDownloadProgress: onProgress }),
  downloadFavorites: (galleryId, onProgress) =>
    api.get(`/photos/gallery/${galleryId}/download-favorites`, { responseType: 'blob', timeout: 600000, onDownloadProgress: onProgress }),
  downloadClientPrintPicks: (galleryId, onProgress) =>
    api.get(`/photos/gallery/${galleryId}/download-client-print-picks`, { responseType: 'blob', timeout: 600000, onDownloadProgress: onProgress }),
  downloadAll: (galleryId, sceneParams, onProgress) =>
    api.get(`/photos/gallery/${galleryId}/download-zip`, { params: sceneParams, responseType: 'blob', timeout: 600000, onDownloadProgress: onProgress }),
};

// ─── Client gallery API factory ────────────────────────────────────────────
// Creates an isolated axios instance using the gallery-client JWT (not the user JWT).
// Use this in ClientGalleryPage so the user's account token is never sent.
export const createClientGalleryApi = (clientToken) => {
  const client = axios.create({
    baseURL: BASE_URL,
    timeout: 15000,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${clientToken}`,
    },
  });
  attachResilience(client);

  return {
    getGallery:       (shareToken)   => client.get(`/galleries/view/${shareToken}`),
    getPhotos:        (galleryId, params) => client.get(`/photos/gallery/${galleryId}`, { params }),
    getScenes:        (galleryId)    => client.get(`/photos/gallery/${galleryId}/scenes`),
    toggleFavorite:   (photoId)      => client.patch(`/photos/${photoId}/client-favorite`),
    togglePrint:      (photoId)      => client.patch(`/photos/${photoId}/client-print`),
    downloadPhoto:    (photoId, onProgress) =>
      client.get(`/photos/${photoId}/download`, { responseType: 'blob', timeout: 60000, onDownloadProgress: onProgress }),
    downloadAll:      (galleryId, sceneParams, onProgress) =>
      client.get(`/photos/gallery/${galleryId}/download-all`, { params: sceneParams, responseType: 'blob', timeout: 600000, onDownloadProgress: onProgress }),
    downloadSelected: (galleryId, type, onProgress) =>
      client.get(`/photos/gallery/${galleryId}/download-selected`, { params: { type }, responseType: 'blob', timeout: 600000, onDownloadProgress: onProgress }),
  };
};

// ─── RSVP ──────────────────────────────────────────────────────────────────
export const rsvpApi = {
  submit:        (slug, data)   => api.post(`/rsvp/${slug}`, data),
  getByToken:    (token)        => api.get(`/rsvp/edit/${token}`),
  updateByToken: (token, data)  => api.put(`/rsvp/edit/${token}`, data),
  getForWedding: (weddingId, p) => api.get(`/rsvp/wedding/${weddingId}`, { params: p }),
  exportCsv:     (weddingId)    => api.get(`/rsvp/wedding/${weddingId}/export`, { responseType: 'blob' }),
  delete:        (rsvpId)       => api.delete(`/rsvp/${rsvpId}`),
};

// ─── Templates ────────────────────────────────────────────────────────────
export const templateApi = {
  getAll:  ()   => api.get('/templates'),
  getById: (id) => api.get(`/templates/${id}`),
};

// ─── Tables ────────────────────────────────────────────────────────────────
export const tableApi = {
  getForWedding:  (weddingId)       => api.get(`/tables/wedding/${weddingId}`),
  getSeatingChart:(weddingId)       => api.get(`/tables/wedding/${weddingId}/seating-chart`),
  getPublic:      (slug, config)    => api.get(`/tables/public/${slug}`, config),
  create:         (weddingId, data) => api.post(`/tables/wedding/${weddingId}`, data),
  bulkCreate:     (weddingId, data) => api.post(`/tables/wedding/${weddingId}/bulk`, data),
  update:         (id, data)        => api.put(`/tables/${id}`, data),
  delete:         (id)              => api.delete(`/tables/${id}`),
  reserveSeat:    (tableId, data)   => api.post(`/tables/${tableId}/reserve`, data),
  reservePartySeats: (data)         => api.post('/tables/reserve-party', data),
  clearPartySeats:  (data)          => api.post('/tables/clear-seats', data),
  releaseSeat:    (tableId, seatN)  => api.delete(`/tables/${tableId}/reserve/${seatN}`),
  moveSeat:       (weddingId, data) => api.post(`/tables/wedding/${weddingId}/move-seat`, data),
  assignSeat:     (weddingId, data) => api.post(`/tables/wedding/${weddingId}/assign-seat`, data),
  findSeatPublic: (slug, name)      => api.post(`/tables/public/${slug}/find-seat`, { name }),
};

// ─── Guest Moments ────────────────────────────────────────────────────────
// ─── Public demo (no signup, read-only; links for social bios) ───────────────
export const demoApi = {
  getInvitation: (params) => api.get('/demo/invitation', { params }),
  getLive:       (params) => api.get('/demo/live', { params }),
};

export const momentsApi = {
  getUploadInfo:    (uploadToken)              => api.get(`/moments/upload/${uploadToken}`),
  getApproved:      (uploadToken, params)      => api.get(`/moments/upload/${uploadToken}/approved`, { params }),
  submitMoment:     (uploadToken, formData, onProgress) =>
    api.post(`/moments/upload/${uploadToken}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
      timeout: 120000,
    }),
  submitText:       (uploadToken, data) =>
    api.post(`/moments/upload/${uploadToken}/text`, data),
  submitAvatar:     (uploadToken, formData) =>
    api.post(`/moments/upload/${uploadToken}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  listMoments:      (galleryId, params)        => api.get(`/moments/gallery/${galleryId}`, { params }),
  reviewMoment:     (momentId, status)         => api.patch(`/moments/${momentId}/review`, { status }),
  deleteMoment:     (momentId)                 => api.delete(`/moments/${momentId}`),
  updateSettings:   (galleryId, data)          => api.put(`/moments/gallery/${galleryId}/settings`, data),
  downloadAll:      (galleryId)                => api.get(`/moments/gallery/${galleryId}/download`, { responseType: 'blob', timeout: 600000 }),
  streamUrl:        (galleryId, token)         =>
    `${process.env.REACT_APP_API_URL || '/api'}/moments/gallery/${galleryId}/stream?token=${encodeURIComponent(token)}`,
};

export const transfersApi = {
  initiate:      (data)   => api.post('/transfers', data),
  listIncoming:  ()       => api.get('/transfers/incoming'),
  listOutgoing:  ()       => api.get('/transfers/outgoing'),
  accept:        (id)     => api.patch(`/transfers/${id}/accept`),
  decline:       (id)     => api.patch(`/transfers/${id}/decline`),
  cancel:        (id)     => api.delete(`/transfers/${id}`),
};

export default api;
